// Gerencia o acesso ao getToken do Clerk para uso nos interceptors do axios.
// O AuthContext registra a função getToken quando inicializa.
//
// getToken({ template }) do Clerk gera um JWT específico do template com um
// round-trip de rede à API do Clerk. Chamar isso antes de CADA request adiciona
// latência morta (o app "pensa" antes de bater no backend). Para evitar:
//   1. Cache: só renova quando o token em cache está ausente ou perto de expirar
//      (lê o exp do próprio JWT). No caminho quente retorna sem tocar no Clerk.
//   2. Dedupe: N requests simultâneos que precisam renovar compartilham uma única
//      Promise de refresh, em vez de disparar N chamadas ao Clerk.

// Renova o token um pouco antes de expirar para não mandar request com token
// já vencido (relógio dessincronizado + tempo de rede).
const EXPIRY_LEEWAY_MS = 30_000;

// Timeout do getToken do Clerk. Se a FAPI do Clerk estolar, o getToken pode
// nunca resolver — sem isso o _inFlight ficaria pendente pra sempre e TODA
// request seguinte travaria nele (só um F5 resetava o módulo). Com timeout,
// cai no token em cache e libera o _inFlight pra próxima tentativa se auto-curar.
const REFRESH_TIMEOUT_MS = 8_000;

// Chave do cache do token do Clerk no localStorage.
const CHAVE_TOKEN = 'konq.token';

let _getToken = null;
let _inFlight = null;

export function registerGetToken(fn) {
  _getToken = fn;
}

// Logout / sessão encerrada: descarta o refresh em voo e o token em cache, senão o
// próximo login no mesmo navegador poderia reaproveitar o token do usuário anterior.
export function clearToken() {
  _inFlight = null;
  _getToken = null;
  localStorage.removeItem(CHAVE_TOKEN);
}

// Lê o exp (epoch em segundos) do payload do JWT sem validar assinatura.
// Retorna null se não conseguir decodificar.
function getExpiryMs(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isUsable(token) {
  if (!token) return false;
  const expMs = getExpiryMs(token);
  // Sem exp legível → trata como inválido pra forçar renovação.
  if (expMs === null) return false;
  return Date.now() < expMs - EXPIRY_LEEWAY_MS;
}

export async function getFreshToken() {
  const cached = localStorage.getItem(CHAVE_TOKEN);

  // Caminho quente: token em cache ainda válido → não toca no Clerk.
  if (isUsable(cached)) {
    return cached;
  }

  // Sem função registrada (auth ainda inicializando) → usa o que tiver.
  if (!_getToken) {
    return cached;
  }

  // Dedupe: se já existe um refresh em andamento, todos aguardam a mesma Promise.
  if (_inFlight) {
    return _inFlight;
  }

  _inFlight = (async () => {
    let timer;
    try {
      const token = await Promise.race([
        _getToken(),
        new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('token refresh timeout')), REFRESH_TIMEOUT_MS);
        }),
      ]);
      if (token) {
        localStorage.setItem(CHAVE_TOKEN, token);
        return token;
      }
      return cached;
    } catch {
      // Timeout ou erro do Clerk → usa o que tiver em cache; o _inFlight é liberado
      // no finally, então a próxima request tenta renovar de novo (auto-cura).
      return cached;
    } finally {
      clearTimeout(timer);
      _inFlight = null;
    }
  })();

  return _inFlight;
}
